<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useAlert, useTrack } from 'dashboard/composables';
import { CAMPAIGN_TYPES } from 'shared/constants/campaign.js';
import { CAMPAIGNS_EVENTS } from 'dashboard/helper/AnalyticsHelper/events.js';

import Button from 'dashboard/components-next/button/Button.vue';
import StepAudience from './steps/StepAudience.vue';
import StepTemplateMapping from './steps/StepTemplateMapping.vue';
import StepReview from './steps/StepReview.vue';
import { useCampaignWizard } from './composables/useCampaignWizard';

const { t } = useI18n();
const router = useRouter();
const store = useStore();

const {
  wizardState,
  canProceedStep1,
  canProceedStep2,
  canSubmit,
  nextStep,
  prevStep,
  resetWizard,
} = useCampaignWizard();

const templateMappingRef = ref(null);
const isSubmitting = ref(false);

const steps = [
  { number: 1, label: 'CAMPAIGN.WHATSAPP.WIZARD.STEP_AUDIENCE' },
  { number: 2, label: 'CAMPAIGN.WHATSAPP.WIZARD.STEP_TEMPLATE' },
  { number: 3, label: 'CAMPAIGN.WHATSAPP.WIZARD.STEP_REVIEW' },
];

const canGoNext = computed(() => {
  if (wizardState.currentStep === 1) return canProceedStep1.value;
  if (wizardState.currentStep === 2) return canProceedStep2.value;
  return false;
});

const formatToUTCString = localDateTime =>
  localDateTime ? new Date(localDateTime).toISOString() : null;

const prepareCampaignPayload = () => {
  const payload = {
    title: wizardState.title,
    inbox_id: wizardState.inboxId,
    scheduled_at: formatToUTCString(
      wizardState.scheduledAt || new Date().toISOString()
    ),
  };

  // Template
  const parserRef = templateMappingRef.value?.templateParserRef;
  const renderedTemplate = parserRef?.renderedTemplate || '';
  const processedParams = parserRef?.processedParams || {};
  const template = wizardState.selectedTemplate;

  payload.template_params = {
    name: template?.name || '',
    namespace: template?.namespace || '',
    category: template?.category || 'UTILITY',
    language: template?.language || 'en_US',
    processed_params: processedParams,
  };
  payload.message = renderedTemplate;

  // Variable mapping
  if (Object.keys(wizardState.variableMapping).length > 0) {
    payload.variable_mapping = wizardState.variableMapping;
  }

  // Audience
  if (wizardState.audienceMode === 'labels') {
    payload.audience_type = 'labels';
    payload.audience = wizardState.selectedLabels.map(id => ({
      id,
      type: 'Label',
    }));
    payload.audience_contacts = [];
    // GK-HUB: N8N segment audience payload
  } else if (wizardState.audienceMode === 'slash_command') {
    payload.audience_type = 'slash_command';
    payload.audience = [];
    payload.audience_contacts = wizardState.segmentContacts;
    // GK-HUB: end N8N segment audience payload
  } else {
    payload.audience_type = 'spreadsheet';
    payload.audience = [];
    payload.audience_contacts = wizardState.uploadedContacts;
  }

  // GK-HUB: Team + optional assignee assignment
  if (wizardState.teamId) {
    payload.team_id = wizardState.teamId;
    if (wizardState.assigneeId) {
      payload.assignee_id = wizardState.assigneeId;
    }
  }
  // GK-HUB: end team + assignee

  // GK-HUB: R6 — Throttle + auto-retry
  if (wizardState.throttleIntervalMs > 0) {
    payload.throttle_interval_ms = wizardState.throttleIntervalMs;
  }
  if (wizardState.maxRetries > 0) {
    payload.max_retries = wizardState.maxRetries;
  }

  return payload;
};

const handleSubmit = async () => {
  isSubmitting.value = true;
  try {
    const payload = prepareCampaignPayload();
    await store.dispatch('campaigns/create', payload);

    useTrack(CAMPAIGNS_EVENTS.CREATE_CAMPAIGN, {
      type: CAMPAIGN_TYPES.ONE_OFF,
    });

    useAlert(t('CAMPAIGN.WHATSAPP.CREATE.FORM.API.SUCCESS_MESSAGE'));
    resetWizard();
    router.push({ name: 'campaigns_dashboard' });
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      t('CAMPAIGN.WHATSAPP.CREATE.FORM.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  } finally {
    isSubmitting.value = false;
  }
};

const handleBack = () => {
  resetWizard();
  router.push({ name: 'campaigns_dashboard' });
};
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <!-- Header -->
    <header class="sticky top-0 z-10 px-6 lg:px-0">
      <div class="w-full max-w-[60rem] mx-auto">
        <div class="flex items-center gap-3 h-16">
          <button
            type="button"
            class="flex items-center gap-1 text-sm text-n-slate-11 hover:text-n-slate-12 transition-colors"
            @click="handleBack"
          >
            <span class="i-lucide-arrow-left text-base" />
            {{ t('CAMPAIGN.WHATSAPP.WIZARD.BACK') }}
          </button>
          <span class="text-xl font-medium text-n-slate-12">
            {{ t('CAMPAIGN.WHATSAPP.WIZARD.TITLE') }}
          </span>
        </div>
      </div>
    </header>

    <!-- Stepper -->
    <div class="px-6 lg:px-0">
      <div class="w-full max-w-[60rem] mx-auto py-4">
        <div class="flex items-center justify-center gap-0">
          <template v-for="(step, index) in steps" :key="step.number">
            <div class="flex items-center gap-2">
              <div
                class="flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium transition-all"
                :class="{
                  'bg-n-teal-9 text-white':
                    wizardState.currentStep === step.number,
                  'bg-n-teal-9/20 text-n-teal-11':
                    wizardState.currentStep > step.number,
                  'bg-n-alpha-black2 text-n-slate-11':
                    wizardState.currentStep < step.number,
                }"
              >
                <span
                  v-if="wizardState.currentStep > step.number"
                  class="i-lucide-check text-sm"
                />
                <span v-else>{{ step.number }}</span>
              </div>
              <span
                class="text-sm hidden sm:inline"
                :class="
                  wizardState.currentStep >= step.number
                    ? 'text-n-slate-12 font-medium'
                    : 'text-n-slate-10'
                "
              >
                {{ t(step.label) }}
              </span>
            </div>
            <div
              v-if="index < steps.length - 1"
              class="w-12 h-px mx-2"
              :class="
                wizardState.currentStep > step.number
                  ? 'bg-n-teal-9'
                  : 'bg-n-weak'
              "
            />
          </template>
        </div>
      </div>
    </div>

    <!-- Step Content -->
    <main class="flex-1 px-6 overflow-y-auto lg:px-0">
      <div class="w-full max-w-[60rem] mx-auto py-4">
        <StepAudience v-show="wizardState.currentStep === 1" />
        <StepTemplateMapping
          v-show="wizardState.currentStep === 2"
          ref="templateMappingRef"
        />
        <StepReview v-show="wizardState.currentStep === 3" />
      </div>
    </main>

    <!-- Footer Navigation -->
    <footer
      class="sticky bottom-0 px-6 lg:px-0 bg-n-surface-1 border-t border-n-weak"
    >
      <div class="w-full max-w-[60rem] mx-auto py-4">
        <div class="flex justify-between items-center">
          <Button
            v-if="wizardState.currentStep > 1"
            variant="faded"
            color="slate"
            :label="t('CAMPAIGN.WHATSAPP.WIZARD.BACK')"
            icon="i-lucide-arrow-left"
            @click="prevStep"
          />
          <div v-else />

          <Button
            v-if="wizardState.currentStep < 3"
            :label="t('CAMPAIGN.WHATSAPP.WIZARD.NEXT')"
            icon="i-lucide-arrow-right"
            trailing-icon
            :disabled="!canGoNext"
            @click="nextStep"
          />
          <Button
            v-else
            :label="t('CAMPAIGN.WHATSAPP.WIZARD.DISPATCH')"
            icon="i-lucide-send"
            :is-loading="isSubmitting"
            :disabled="!canSubmit || isSubmitting"
            @click="handleSubmit"
          />
        </div>
      </div>
    </footer>
  </section>
</template>
